<template>
  <div class="page-table scrollable only-y">
    <div class="no-table-box page-header">
      <el-row class="hidden-sm-and-up pb-15">
        <h1>
          <v-svg class="voxo-green" sprite="ambulance" />
          e911 Locations
        </h1>
      </el-row>
      <el-row class="hidden-xs-only pb-15">
        <el-col :span="12">
          <h1>
            <v-svg class="voxo-green" sprite="ambulance" />
            e911 Locations
          </h1>
        </el-col>
        <el-col class="text-right" :span="12">
          <el-tag>
            <i class="el-icon-info" style="margin-right: 4px" />
            Total: {{ totalRecords }}
          </el-tag>
        </el-col>
      </el-row>
      <div class="flex pb-20">
        <div class="box">
          <el-input v-model="search" placeholder="Search" clearable> </el-input>
          <el-button class="ml-10 hidden-xs-only" @click="doExport">
            Export
          </el-button>
          <el-button class="ml-10" @click="showModal('Create')"
            >Add New</el-button
          >
        </div>
        <div v-if="tableData.length" class="box right">
          <el-pagination
            small
            background
            layout="prev, pager, next"
            @current-change="pageSet"
            @next-click="pageNext"
            @prev-click="pagePrevious"
            :current-page="page"
            :page-size="recordsPerPage"
            :pager-count="5"
            :total="totalRecords"
          />
        </div>
      </div>

      <div class="vxe-table-height card-base card-shadow--medium">
        <vxe-grid
          round
          border="inner"
          ref="xGrid"
          height="100%"
          auto-resize
          empty-text="No Data"
          show-overflow="tooltip"
          highlight-hover-row
          @cell-click="cellClicked"
          @cell-mouseenter="rowHover"
          @cell-mouseleave="rowLeave"
          :data="tableData"
          :scroll-y="{ enabled: true, gt: 50, oSize: 0 }"
          :loading="tableLoading"
          :columns="tableColumns"
        />
      </div>
    </div>

    <location-form
      :visible="modalVisible"
      :context="modalContext"
      :recordId="modalModelId"
      @modalClosed="onModalClosed"
    >
    </location-form>
  </div>
</template>

<script>
import { debounce } from "lodash";
import { ExportToCsv } from "export-to-csv";
import LocationForm from "./components/form";
import { apiV2Host } from "@/feathers/feathers-client";

export default {
  name: "LocationsTable",
  components: { LocationForm },
  data() {
    return {
      search: "",
      totalRecords: 0,
      recordsPerPage: 50,
      page: 1,
      maxPage: 1,
      tableData: [],
      tableLoading: false,

      allLocations: [],
      visibleActionsId: "",
      modalModelId: "",
      modalVisible: false,
      modalContext: "",
      tableColumns: [
        {
          field: "sprite",
          title: "",
          width: 50,
          slots: {
            default: () => {
              return [
                <div class="voxo-square-icon voxo-bg-btn">
                  <v-svg class="voxo-green" sprite="ambulance" />
                </div>,
              ];
            },
          },
        },
        {
          field: "name",
          title: "Name",
          minWidth: 200,
          sortable: true,
        },
        {
          field: "address",
          title: "Address",
          minWidth: 300,
          sortable: true,
          showOverflow: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.addressFormatter(row.address)}</span>];
            },
          },
        },
        {
          field: "Coords",
          title: "Coords",
          minWidth: 100,
          sortable: false,
          showOverflow: true,
          slots: {
            default: ({ row }) => {
              return [<span>{this.coordinates(row.address)}</span>];
            },
          },
        },
        {
          field: "actions",
          title: "",
          minWidth: "150",
          showOverflow: false,
          slots: {
            default: ({ row }) => {
              return [
                <div v-show={row.id === this.visibleActionsId}>
                  <el-button
                    id={"edit-" + row.id}
                    icon="el-icon-edit"
                    onClick={() => this.showModal("Edit", row.id)}
                    class="table-action-button icon-blue"
                  />
                  <el-button
                    id={"delete-" + row.id}
                    icon="el-icon-delete"
                    onClick={() => this.deleteRecord(row.id)}
                    class="table-action-button icon-red"
                  />
                </div>,
              ];
            },
          },
        },
      ],
    };
  },
  computed: {
    selectedTenant() {
      return this.$store.state.selectedTenant;
    },
    user() {
      return this.$store.state.auth.user;
    },
  },
  watch: {
    selectedTenant() {
      this.queryLocations();
    },

    user() {
      this.queryLocations();
    },

    page: debounce(function () {
      this.queryLocations();
    }, 500),

    search: debounce(function () {
      this.queryLocations();
    }, 500),
  },

  methods: {
    onModalClosed(location) {
      this.modalVisible = false;

      if (!location) return;

      if (this.modalContext === "Create") {
        this.tableData.unshift(location);
      }

      if (this.modalContext === "Edit") {
        const updateIndex = this.tableData.findIndex(
          (item) => item.id === location.id
        );

        if (updateIndex >= 0) {
          this.tableData.splice(updateIndex, 1, location);
        }
      }
    },

    // Pagination
    pageNext() {
      if (this.page < this.maxPage) {
        this.page += 1;
      }
    },

    pagePrevious() {
      if (this.page > 1) {
        this.page -= 1;
      }
    },

    pageSet(page) {
      this.page = page;
    },

    // General Form and Table Operations
    doExport() {
      const data = [...this.tableData];
      if (data.length) {
        const writeData = data.map((item) => ({
          name: item.name,
          address: this.addressFormatter(item.address),
        }));

        const options = {
          filename: "e911_locations",
          showLabels: true,
          headers: ["Name", "Address"],
        };

        const csvExporter = new ExportToCsv(options);
        csvExporter.generateCsv(writeData);
      }
    },

    addressFormatter(address) {
      return (
        address.streetNum +
        " " +
        address.streetInfo +
        " " +
        address.location +
        " " +
        address.city +
        " " +
        address.state +
        " " +
        address.postalCode
      ).replace(/undefined/g, "");
    },

    coordinates(address) {
      return address.loc;
    },

    async queryLocations() {
      this.tableLoading = true;
      this.tableData = [];

      try {
        let url = `${apiV2Host}/admin/emergency-locations?tenantId=${this.selectedTenant.id}&recordsPerPage=${this.recordsPerPage}&page=${this.page}`;

        if (this.search && this.search.length) {
          url += `&name=${this.search}`;
        }

        const res = await fetch(url, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
          },
        });

        const data = await res.json();

        const { records, totalRecords, maxPage } = data;

        this.tableData = [...records];
        this.totalRecords = totalRecords;
        this.maxPage = maxPage;
        this.tableLoading = false;
      } catch (err) {
        this.tableData = [];
        this.tableLoading = false;
      }
    },

    // Delete Record
    deleteRecord(id) {
      this.$confirm(
        "Permanently delete this E911 location? No going back!",
        "Warning",
        {
          confirmButtonText: "Yes",
          cancelButtonText: "Cancel",
          type: "warning",
        }
      )
        .then(async () => {
          this.tableLoading = true;

          try {
            const res = await fetch(
              `${apiV2Host}/admin/emergency-locations/${id}`,
              {
                method: "DELETE",
                headers: {
                  Authorization: `Bearer ${this.$store.state.auth.accessToken}`,
                  Accept: "application/json",
                },
              }
            );
            const response = await res.json();

            this.tableLoading = false;

            if (!res.ok) {
              this.$notify({
                title: response.name,
                message: response.message,
                type: "error",
              });
              return;
            }

            this.queryLocations();

            this.$notify({
              title: "Success",
              message: "E911 " + response.name + " has been removed!",
              type: "success",
            });
          } catch (err) {
            this.tableLoading = false;
          }
        })
        .catch(() => {
          this.$message({
            type: "info",
            message: "Delete canceled",
          });
        });
    },

    cellClicked(data) {
      if (data.column.property !== "actions") {
        this.showModal("Edit", data.row.id);
      }
    },

    rowHover(data) {
      this.visibleActionsId = data.row.id;
    },

    rowLeave() {
      this.visibleActionsId = "";
    },

    showModal(context, editId = null) {
      this.modalVisible = true;
      this.modalContext = context;
      this.modalModelId = editId;
    },
  },

  mounted() {
    if (this.user) {
      this.queryLocations();
    }
  },
};
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/variables";

.current-address-details {
  max-width: 600px;
  margin: 10px auto;
}
</style>
